import { Alert, Box, Button, Chip, Divider, Grid, List, ListItem, ListItemText, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getAssociations, getAssociationFeeById } from 'src/shared/services/associationService';
import { AssociationFee } from 'src/modules/associations/models/associationFee';
import { Association } from 'src/shared/models/association/association';
import PaidIcon from '@mui/icons-material/Paid';

type OnboardingStepMemberFeeProps = {
  onNavigateAway?: () => void;
};

interface AssociationWithFee {
  association: Association;
  fee: AssociationFee | null;
}

export const OnboardingStepMemberFee = ({ onNavigateAway }: OnboardingStepMemberFeeProps) => {
  const { t } = useTranslation(['onboarding']);
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [associationsWithFees, setAssociationsWithFees] = useState<AssociationWithFee[]>([]);

  const isFederation = (currentUser?.districtCount ?? 0) > 0 || (currentUser?.associationCount ?? 0) > 1;
  const isCentralConfig = currentUser?.customer?.centralConfig === true;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const assRes = await getAssociations();
      const associations: Association[] = assRes.data.results || [];

      const withFees = await Promise.all(
        associations.slice(0, 10).map(async (ass) => {
          try {
            const feeRes = await getAssociationFeeById(String(ass.localAss_id));
            return { association: ass, fee: feeRes.data };
          } catch {
            return { association: ass, fee: null };
          }
        }),
      );

      if (isMounted()) setAssociationsWithFees(withFees);
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFeeDescription = (fee: AssociationFee | null): string => {
    if (!fee || !fee.paymentMethod) return t('onboarding:stepContent.memberFee.noFeeConfigured');

    switch (fee.paymentMethod) {
      case 1: // AllMembersSameFee
        return `${t('onboarding:stepContent.memberFee.methodSameFee')}: ${fee.sameFeeForAll ?? 0} kr`;
      case 2: // HeadAndFamily
        return `${t('onboarding:stepContent.memberFee.feeByFamily')}: ${fee.mainAndFamily?.feeMainMember ?? 0} kr / ${
          fee.mainAndFamily?.feeFamilyMember ?? 0
        } kr`;
      case 3: // FamilyLadder
        if (fee.familySteps && fee.familySteps.length > 0) {
          const steps = fee.familySteps.map((s) => `${s.fee ?? 0} kr`).join(', ');
          return `${t('onboarding:stepContent.memberFee.feeByLadder')}: ${steps}`;
        }
        return t('onboarding:stepContent.memberFee.feeByLadder');
      case 4: // HouseholdSameFee
        return `${t('onboarding:stepContent.memberFee.feePerHousehold')}: ${fee.oneFeeForHousehold ?? 0} kr`;
      case 5: // ProfileBasedFee
        return t('onboarding:stepContent.memberFee.feeByProfile');
      default:
        return t('onboarding:stepContent.memberFee.noFeeConfigured');
    }
  };

  const renderProfileFees = (fee: AssociationFee) => {
    if (!fee.feeBasedOnProfile || fee.feeBasedOnProfile.length === 0) return null;
    return (
      <Box sx={{ pl: 2, mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {fee.feeBasedOnProfile.map((pf, idx) => (
          <Chip
            key={pf.memberProfileItem_id || idx}
            label={`${pf.profile || pf.profileGroup || '?'}: ${pf.fee ?? 0} kr`}
            size='small'
            variant='outlined'
          />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.memberFee.description')}
            </Typography>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {(() => {
                if (isFederation) {
                  return isCentralConfig
                    ? t('onboarding:stepContent.memberFee.hintFederationCentral')
                    : t('onboarding:stepContent.memberFee.hintFederationIndividual');
                }
                return t('onboarding:stepContent.memberFee.hintSingle');
              })()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          {isFederation ? (
            <Alert severity='warning'>{t('onboarding:stepContent.memberFee.federationHint')}</Alert>
          ) : (
            <>
              <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
                {t('onboarding:stepContent.memberFee.currentFees')}
              </Typography>
              <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
                <Alert severity='warning' sx={{ mb: 2 }}>
                  {t('onboarding:stepContent.memberFee.editHintPrefix')}{' '}
                  <Button
                    variant='text'
                    size='small'
                    sx={{
                      p: 0,
                      minWidth: 0,
                      textTransform: 'none',
                      fontWeight: 600,
                      verticalAlign: 'baseline',
                      fontSize: 'inherit',
                      lineHeight: 'inherit',
                    }}
                    onClick={() => {
                      if (onNavigateAway) onNavigateAway();
                    }}
                  >
                    {t('onboarding:stepContent.memberFee.editHintLink')}
                  </Button>{' '}
                  {t('onboarding:stepContent.memberFee.editHintSuffix')}
                </Alert>
                {loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Skeleton variant='rounded' height={48} />
                    <Skeleton variant='rounded' height={48} />
                  </Box>
                ) : (
                  <List disablePadding>
                    {associationsWithFees.map(({ association, fee }) => (
                      <Box key={association.localAss_id}>
                        <ListItem sx={{ px: 1 }}>
                          <PaidIcon sx={{ color: 'text.secondary', mr: 1.5 }} fontSize='small' />
                          <ListItemText
                            primary={association.localAss}
                            secondary={getFeeDescription(fee)}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                        </ListItem>
                        {fee && renderProfileFees(fee)}
                        <Divider />
                      </Box>
                    ))}
                  </List>
                )}
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
